import { cn } from '@/lib/utils'

/**
 * Human labels for the codes the Incident Desk renders.
 *
 * ## This file never widens the vocabulary
 *
 * Every code list is SERVED (`/incidents/vocabulary/*`). This maps a code the server sent
 * to words; a code with no entry falls back to a title-cased version of itself, so a
 * constant added on the server renders imperfectly rather than disappearing. It can never
 * ADD an option — every `<select>` is still driven by the served array. A hand-written
 * option list that drifts from the server is the defect `VocabularyContractTest` and
 * `mock-route-check.mjs` exist to prevent.
 *
 * ## Severity is ORDERED; status is a STATE
 *
 * `severityTone` returns an index into the `--ordinal-*` ramp, because LOW < MEDIUM <
 * HIGH < CRITICAL and ordered data on a categorical palette is the most common chart
 * error there is. `statusTone` returns a semantic class pair and always ships alongside
 * the label — colour never carries the meaning on its own.
 */

const LABELS: Record<string, string> = {
  // ── category ────────────────────────────────────────────────────────────
  VIOLENCE: 'Violence',
  INTIMIDATION: 'Intimidation',
  FRAUD: 'Fraud',
  HATE_SPEECH: 'Hate speech',
  LOGISTICS: 'Logistics failure',
  TECHNICAL: 'Technical failure',
  VOTE_BRIBERY: 'Vote bribery',
  BALLOT_STUFFING: 'Ballot stuffing',
  VOTER_SUPPRESSION: 'Voter suppression',
  MISINFORMATION: 'Misinformation',
  PROPERTY_DAMAGE: 'Property damage',
  OBSTRUCTION: 'Obstruction',
  OTHER: 'Other',

  // ── severity — ORDERED ──────────────────────────────────────────────────
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',

  // ── priority. The number is the point; "P1" alone tells a new user nothing. ──
  P1: 'P1 — immediate',
  P2: 'P2 — high',
  P3: 'P3 — standard',
  P4: 'P4 — low',

  // ── status: where it is in the WORKFLOW ─────────────────────────────────
  DRAFT: 'Draft',
  OPEN: 'Open',
  ACKNOWLEDGED: 'Acknowledged',
  INVESTIGATING: 'Investigating',
  ESCALATED: 'Escalated',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',

  // ── verification: how much we BELIEVE it. A separate axis, on purpose. ───
  UNVERIFIED: 'Unverified',
  CORROBORATED: 'Corroborated',
  VERIFIED: 'Verified',
  DISPUTED: 'Disputed',
  REFUTED: 'Refuted — did not happen',

  // ── resolution ──────────────────────────────────────────────────────────
  SUBSTANTIATED: 'Substantiated',
  UNSUBSTANTIATED: 'Not substantiated',
  DUPLICATE: 'Duplicate of another report',
  REFERRED: 'Referred to another body',
  NO_ACTION: 'No action taken',

  // ── media coverage ──────────────────────────────────────────────────────
  NONE: 'None',
  LOCAL: 'Local media',
  NATIONAL: 'National media',
  INTERNATIONAL: 'International media',

  // ── evidence kind ───────────────────────────────────────────────────────
  PHOTO: 'Photo',
  VIDEO: 'Video',
  AUDIO: 'Audio',
  DOCUMENT: 'Document',
  SCREENSHOT: 'Screenshot',
  LINK: 'Link',
  LIVE_FEED: 'Live feed',
  WITNESS_STATEMENT: 'Witness statement',

  // ── timeline action ─────────────────────────────────────────────────────
  CREATED: 'Created',
  SUBMITTED: 'Submitted',
  UPDATED: 'Updated',
  STATUS_CHANGED: 'Status changed',
  ASSIGNED: 'Assigned',
  VERIFICATION_CHANGED: 'Verification changed',
  EVIDENCE_ADDED: 'Evidence added',
  EVIDENCE_WITHDRAWN: 'Evidence withdrawn',
  COMMENTED: 'Comment',
  REOPENED: 'Reopened',
  LIVE_FEED_ATTACHED: 'Live feed attached',

  // ── how the coordinate was obtained. This IS evidence — see plan §3.5. ──
  DEVICE_GPS: 'Device GPS',
  MAP_PICK: 'Picked on the map',
  UNIT_CENTROID: 'Centre of the unit',

  // ── how precisely the time is known ─────────────────────────────────────
  EXACT: 'To the minute',
  HOUR: 'To the hour',
  DAY: 'To the day',
  APPROX: 'Approximately',
}

/*
   NONE, ESCALATED, ACKNOWLEDGED, VERIFIED and DISPUTED each appear in two vocabularies
   with the same word in both, so one flat map is honest here. Anywhere the same code
   would need two different words, the caller must reach for a scoped label instead of
   widening this map — a single map that lies for one of its two meanings is worse than
   two maps.
*/

/** A code the server sent → words. Unknown codes title-case rather than vanish. */
export function label(code?: string | null): string {
  if (!code) return '—'
  return LABELS[code] ?? code.charAt(0) + code.slice(1).toLowerCase().replace(/_/g, ' ')
}

/** For a `<select>` driven by a served array. */
export function options(codes: string[] | null | undefined) {
  return (codes ?? []).map((c) => ({ value: c, label: label(c) }))
}

// ── ordered scales ──────────────────────────────────────────────────────────

/** Rank within the ordered severity scale. Drives the `--ordinal-*` ramp. */
export const SEVERITY_ORDER = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']
/** UNVERIFIED → REFUTED is a ladder of belief, not a set of categories. */
export const VERIFICATION_ORDER = ['UNVERIFIED', 'DISPUTED', 'CORROBORATED', 'VERIFIED', 'REFUTED']
/** The workflow, in the order a desk walks it. Drives the funnel. */
export const STATUS_FLOW = ['OPEN', 'ACKNOWLEDGED', 'INVESTIGATING', 'ESCALATED', 'RESOLVED', 'CLOSED']

/**
 * The ordinal token for a severity, 1-based.
 *
 * An unknown code lands on the middle step rather than at either end: guessing CRITICAL
 * for a code we do not recognise would raise an alarm that nothing supports, and guessing
 * LOW would bury one.
 */
export function severityStep(code?: string | null): number {
  const i = SEVERITY_ORDER.indexOf(code ?? '')
  return i < 0 ? 3 : i + 2      // LOW→2 … CRITICAL→5, so LOW is not the palest possible
}

/**
 * The colour a severity gets ON A MAP, and how big its marker is.
 *
 * ## Why not the `--ordinal-*` ramp used in the charts
 *
 * That ramp separates its steps by lightness within a single hue, which works against a
 * card of known colour and fails against a basemap. Measured on the adjacent-pair basis it
 * scored a worst normal-vision ΔE of 10.9 against a floor of 15, and failed the chroma
 * floor outright — over satellite imagery the four steps read as four shades of the same
 * grey-brown. This ramp scores 18.0 normal-vision and 13.5 CVD while staying strictly
 * monotone in lightness, so it is still ORDERED rather than categorical.
 *
 * ## Size is the second channel, and it is the one that always works
 *
 * A marker also grows with severity. Hue and lightness can both be defeated — by colour
 * vision, by greyscale printing, by a bright patch of imagery underneath — and size cannot.
 * A reader who can perceive none of the colour still sees that the big dots are the bad
 * ones.
 */
export function severityMarker(code?: string | null): { color: string; size: number } {
  switch (code) {
    case 'LOW': return { color: 'var(--severity-low)', size: 11 }
    case 'MEDIUM': return { color: 'var(--severity-medium)', size: 14 }
    case 'HIGH': return { color: 'var(--severity-high)', size: 18 }
    case 'CRITICAL': return { color: 'var(--severity-critical)', size: 22 }
    // An unknown code takes the middle size and a neutral fill: guessing CRITICAL raises
    // an alarm nothing supports, and guessing LOW buries one.
    default: return { color: 'var(--muted-foreground)', size: 14 }
  }
}

/** Sorts a set of codes into the scale's own order, unknowns last, alphabetically. */
export function inScaleOrder(codes: string[], scale: string[]): string[] {
  return [...codes].sort((a, b) => {
    const ia = scale.indexOf(a)
    const ib = scale.indexOf(b)
    if (ia < 0 && ib < 0) return a.localeCompare(b)
    if (ia < 0) return 1
    if (ib < 0) return -1
    return ia - ib
  })
}

// ── chips ───────────────────────────────────────────────────────────────────

const CHIP = 'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-erp-xs font-medium whitespace-nowrap'

/**
 * Severity, on the ordered ramp.
 *
 * The swatch is the ordinal step; the WORD sits next to it. A reader who cannot separate
 * two steps of one hue still has the whole answer, which is the rule that lets an ordered
 * ramp be used at all.
 */
export function SeverityChip({ code }: { code?: string | null }) {
  return (
    <span className={cn(CHIP, 'border-border bg-muted/40')}>
      <span aria-hidden className="h-2 w-2 rounded-full shrink-0"
        style={{ background: `var(--ordinal-${severityStep(code)})` }} />
      {label(code)}
    </span>
  )
}

/** Status: a state, so it is a reserved semantic colour AND always a word. */
export function StatusChip({ code }: { code?: string | null }) {
  return <span className={cn(CHIP, statusTone(code))}>{label(code)}</span>
}

function statusTone(code?: string | null): string {
  switch (code) {
    case 'DRAFT': return 'border-border bg-muted/50 text-muted-foreground'
    case 'OPEN': return 'border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
    case 'ACKNOWLEDGED':
    case 'INVESTIGATING': return 'border-sky-300 bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300'
    case 'ESCALATED': return 'border-red-300 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
    case 'RESOLVED': return 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
    case 'CLOSED': return 'border-border bg-muted/60 text-muted-foreground'
    default: return 'border-border bg-muted/40'
  }
}

/**
 * Verification, distinct from status on purpose.
 *
 * The desk's one idea is that "where it is in the process" and "how much we believe it"
 * are different questions. Two chips that looked alike would quietly re-merge them, so
 * this one is outlined rather than filled and always carries the word.
 */
export function VerificationChip({ code }: { code?: string | null }) {
  const tone =
    code === 'VERIFIED' ? 'border-emerald-400 text-emerald-800 dark:text-emerald-300'
      : code === 'CORROBORATED' ? 'border-sky-400 text-sky-800 dark:text-sky-300'
        : code === 'DISPUTED' ? 'border-amber-400 text-amber-800 dark:text-amber-300'
          : code === 'REFUTED' ? 'border-red-400 text-red-700 dark:text-red-300'
            : 'border-border text-muted-foreground'
  return <span className={cn(CHIP, 'bg-transparent', tone)}>{label(code)}</span>
}

/**
 * How old, in words a desk acts on.
 *
 * An age is the number the ageing list is sorted by, so it is rendered as one string
 * everywhere rather than left to each table to format differently.
 */
export function ageOf(iso?: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return '—'
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}
